// Redesigned Header with Sidebar for Mobile, Same Header for Desktop
import { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Button,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TranslateIcon from "@mui/icons-material/Translate";
import logo from "../../assets/images/logo.svg";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { LoginButton } from "components/shared/LoginButton";
import { ReusableMenu } from "components/ReusableMenu";
import { routes, LanguageKeys } from "constants/index";
import { theme } from "theme/theme";
import { Container, Button as CoreButton } from "components/Core";

const Header = ({
  header,
  consultButton,
  navigateTo,
  hideSchoolNav,
  hideSignIn,
}) => {
  const { t, i18n } = useTranslation(["common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (searchParams.get("lang")) {
      i18next.changeLanguage(searchParams.get("lang"));
    } else if (localStorage.getItem("i18nextLng")?.length > 2) {
      i18next.changeLanguage("ar");
    }
    setSearchParams({ lang: localStorage.getItem("i18nextLng") });
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setSearchParams({ lang });
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const currentLang = localStorage.getItem("i18nextLng") || "en";

  const drawerItems = (
    <Box width={250} p={2} role="presentation">
      <List>
        {consultButton && (
          <ListItem sx={{ px: 0 }} disablePadding>
            <ListItemButton
              sx={{ px: 0 }}
              component={Link}
              to="/join-our-network"
            >
              <ListItemText primary={t("buttons.becomeTherapist")} />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem sx={{ px: 0 }} disablePadding>
          <ListItemButton sx={{ px: 0 }} component={Link} to="/courses">
            <ListItemText primary={t("buttons.courses")} />
          </ListItemButton>
        </ListItem>
        {!hideSchoolNav && (
          <ListItem sx={{ px: 0 }} disablePadding>
            <ListItemButton sx={{ px: 0 }} component={Link} to={routes.school}>
              <ListItemText primary={t("buttons.forSchool")} />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem sx={{ px: 0 }} disablePadding>
          <ListItemButton
            sx={{ px: 0 }}
            component="a"
            href="https://resources.numuw.com/"
            target="_blank"
          >
            <ListItemText primary={t("resources")} />
          </ListItemButton>
        </ListItem>
        {consultButton && (
          <ListItem sx={{ px: 0 }}>
            <CoreButton
              variant="smallCTA"
              onClick={() => {
                searchParams.set("modal", "hint");
                setSearchParams(searchParams);
                setDrawerOpen(false);
              }}
            >
              {t("buttons.getStartedToday")}
            </CoreButton>
          </ListItem>
        )}
        {!hideSignIn && (
          <ListItem sx={{ px: 0 }}>
            <LoginButton label={t("buttons.signIn")} fullWidth />
          </ListItem>
        )}
        <ListItem sx={{ px: 0 }}>
          <ReusableMenu
            menuItemSx={{ color: "#000" }}
            ButtonLabel={(props) => (
              <Button
                color="secondary"
                startIcon={<TranslateIcon />}
                {...props}
                fullWidth
              >
                {LanguageKeys[currentLang]}
              </Button>
            )}
            menuItems={[
              { label: "English", onClick: () => handleLanguageChange("en") },
              { label: "العربية", onClick: () => handleLanguageChange("ar") },
            ]}
          />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Container>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          mb: 2,
          bgcolor: "transparent",
          maxWidth: "1440px",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
            {navigateTo ? (
              <IconButton component={Link} to={navigateTo} edge="start">
                <ArrowBackIcon />
              </IconButton>
            ) : (
              <Link to="/">
                <img src={logo} alt="logo" style={{ width: 120 }} />
              </Link>
            )}
          </Box>

          {!isMobile && header && (
            <Box display="flex" alignItems="center" gap={2}>
              {consultButton && (
                <Link to="/join-our-network">
                  <Button
                    sx={{
                      textTransform: "capitalize",
                      color: theme.colors.primary,
                    }}
                  >
                    {t("buttons.joinNetwork")}
                  </Button>
                </Link>
              )}
              <Link to="/courses">
                <Button
                  sx={{
                    textTransform: "capitalize",
                    color: theme.colors.primary,
                  }}
                >
                  {t("buttons.courses")}
                </Button>
              </Link>
              {!hideSchoolNav && (
                <Link to={routes.school}>
                  <Button
                    sx={{
                      textTransform: "capitalize",
                      color: theme.colors.primary,
                    }}
                  >
                    {t("buttons.forSchool")}
                  </Button>
                </Link>
              )}
              <a
                href="https://resources.numuw.com/"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  sx={{
                    textTransform: "capitalize",
                    color: theme.colors.primary,
                  }}
                >
                  {t("resources")}
                </Button>
              </a>
              {consultButton && (
                <CoreButton
                  ml={"44px"}
                  variant="smallCTA"
                  onClick={() => {
                    searchParams.set("modal", "hint");
                    setSearchParams(searchParams);
                  }}
                >
                  {t("buttons.getStartedToday")}
                </CoreButton>
              )}
              {!hideSignIn && <LoginButton label={t("buttons.signIn")} />}
              <ReusableMenu
                ButtonLabel={(props) => (
                  <Button
                    color="secondary"
                    startIcon={<TranslateIcon />}
                    {...props}
                  >
                    {LanguageKeys[currentLang]}
                  </Button>
                )}
                menuItems={[
                  {
                    label: "English",
                    onClick: () => handleLanguageChange("en"),
                  },
                  {
                    label: "العربية",
                    onClick: () => handleLanguageChange("ar"),
                  },
                ]}
              />
            </Box>
          )}

          {isMobile && (
            <IconButton edge="end" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>

        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
          {drawerItems}
        </Drawer>
      </AppBar>
    </Container>
  );
};

export default Header;
